import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getCurrentUser,
  signOut,
  fetchAuthSession,
  fetchUserAttributes,
  AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

const SESSION_MAX_DURATION_MS = 60 * 60 * 1000; // 1 hour

interface AuthContextType {
  user: AuthUser | null;
  /** Human-readable identifier for the signed-in user (name or email). */
  displayName: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  handleSignOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  displayName: null,
  isAuthenticated: false,
  isLoading: true,
  sessionExpired: false,
  handleSignOut: async () => {},
});

/**
 * Picks a human-readable display name from Cognito user attributes / sign-in info.
 * Preference order: given_name + family_name > name > email > preferred_username > loginId.
 * Returns null if nothing usable is available (so callers can fall back to 'User').
 */
function pickDisplayName(
  attributes: Record<string, string | undefined>,
  loginId: string | undefined
): string | null {
  const given = attributes.given_name?.trim();
  const family = attributes.family_name?.trim();
  if (given && family) return `${given} ${family}`;
  if (given) return given;

  const name = attributes.name?.trim();
  if (name) return name;

  const email = attributes.email?.trim();
  if (email) return email;

  const preferred = attributes.preferred_username?.trim();
  if (preferred && !isUuid(preferred)) return preferred;

  if (loginId && !isUuid(loginId)) return loginId;

  return null;
}

/** Detects a UUID v1-v5 string so we don't display the Cognito sub as a name. */
function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export function useAuth(): AuthContextType {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      setDisplayName(null);
      setSessionStartTime(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (!session.tokens) {
        setUser(null);
        setDisplayName(null);
        return;
      }

      setUser(currentUser);

      // Fetch user attributes to derive a human-readable display name.
      // Fall back to ID token claims if the attributes call fails.
      try {
        const attributes = await fetchUserAttributes();
        setDisplayName(pickDisplayName(attributes, currentUser.signInDetails?.loginId));
      } catch {
        const claims = session.tokens?.idToken?.payload ?? {};
        setDisplayName(
          pickDisplayName(
            {
              email: typeof claims.email === 'string' ? claims.email : undefined,
              given_name: typeof claims.given_name === 'string' ? claims.given_name : undefined,
              family_name: typeof claims.family_name === 'string' ? claims.family_name : undefined,
              name: typeof claims.name === 'string' ? claims.name : undefined,
              preferred_username:
                typeof claims.preferred_username === 'string' ? claims.preferred_username : undefined,
            },
            currentUser.signInDetails?.loginId
          )
        );
      }

      if (!sessionStartTime) {
        setSessionStartTime(Date.now());
      }
    } catch {
      setUser(null);
      setDisplayName(null);
    } finally {
      setIsLoading(false);
    }
  }, [sessionStartTime]);

  // Check session expiry based on 1-hour max duration
  useEffect(() => {
    if (!sessionStartTime || !user) return;

    const timeRemaining = SESSION_MAX_DURATION_MS - (Date.now() - sessionStartTime);

    if (timeRemaining <= 0) {
      setSessionExpired(true);
      handleSignOut();
      return;
    }

    const timer = setTimeout(() => {
      setSessionExpired(true);
      handleSignOut();
    }, timeRemaining);

    return () => clearTimeout(timer);
  }, [sessionStartTime, user, handleSignOut]);

  // Listen to auth events
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          setSessionExpired(false);
          setSessionStartTime(Date.now());
          checkSession();
          break;
        case 'signedOut':
          setUser(null);
          setDisplayName(null);
          setSessionStartTime(null);
          break;
        case 'tokenRefresh_failure':
          setSessionExpired(true);
          setUser(null);
          setDisplayName(null);
          setSessionStartTime(null);
          break;
      }
    });

    return unsubscribe;
  }, [checkSession]);

  // Initial session check
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const value: AuthContextType = {
    user,
    displayName,
    isAuthenticated: !!user,
    isLoading,
    sessionExpired,
    handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
